import { getCommonWidget } from '../../dashWidgetHelper'
import { commonWidgets, NETPAY_COLORS_NPS, NETPAY_SCALE_3_COLORS } from '../commonWidgetsNetpay'
import { List } from 'immutable';

// --------------------------------------------------------
// DICCIONARIOS DE ETIQUETAS
// --------------------------------------------------------
const CSAT_LABELS: Record<string, string> = {
    "5": "Extremadamente satisfecho",
    "4": "Satisfecho",
    "3": "Ni satisfecho ni insatisfecho",
    "2": "Insatisfecho",
    "1": "Extremadamente insatisfecho",
    "0": "NS / NC"
};

const CES_LABELS: Record<string, string> = {
    "5": "Extremadamente fácil",
    "4": "Fácil",
    "3": "Ni fácil ni difícil",
    "2": "Difícil",
    "1": "Extremadamente complicado",
    "0": "NS / NC"
};

// --------------------------------------------------------
// FUNCIÓN HELPER (Mapeo de datos para pasteles)
// --------------------------------------------------------
const mapWithLabels = (data: any, dict: Record<string, string>) => {
    if (!Array.isArray(data)) return [];

    return data.map((item: any, index: number, array: any[]) => {
        const keyCandidate = item.groupId ?? item.groupName ?? item.value ?? item.name;
        const key = keyCandidate !== undefined && keyCandidate !== null ? String(keyCandidate).trim() : '';

        let labelText = key;

        if (dict && dict[key]) {
            labelText = dict[key];
        } else if (item.groupName && String(item.groupName).trim() !== '') {
            labelText = String(item.groupName);
        }

        if (!labelText || labelText === 'undefined') labelText = `Sin respuesta`;

        const count = item.count !== undefined ? item.count : 0;
        let finalValue = item.value;

        if (array.length === 1) {
            finalValue = 100;
        } else if (finalValue === undefined) {
            finalValue = count;
        }

        return {
            ...item,
            value: finalValue,
            groupName: labelText,
            label: count > 0 ? `${labelText} (${count})` : labelText
        };
    });
};

const NETPAY_SCALE_5_COLORS: any = {
    "Extremadamente satisfecho": '#22a06b',
    "Satisfecho": '#a3d977',
    "Ni satisfecho ni insatisfecho": '#f5a524',
    "Insatisfecho": '#f08591',
    "Extremadamente insatisfecho": '#e74c5c',
    
    "Extremadamente fácil": '#22a06b',
    "Fácil": '#a3d977',
    "Ni fácil ni difícil": '#f5a524',
    "Difícil": '#f08591',
    "Extremadamente complicado": '#e74c5c',
    
    "NS / NC": '#cccccc',
    1: '#e74c5c',
    2: '#f08591',
    3: '#f5a524',
    4: '#a3d977',
    5: '#22a06b',
};

const mapGaugeData = (data: any, dict: Record<string, string>) => {
    const actualData = Array.isArray(data) ? data[0] : data;
    if (!actualData || !actualData.details) return actualData;

    let details = actualData.details;

    // Sort by groupId descending (5, 4, 3, 2, 1)
    let sortedDetails = [...details].sort((a: any, b: any) => (b.groupId || 0) - (a.groupId || 0));

    // Map labels
    sortedDetails = sortedDetails.map((item: any) => {
        const key = item.groupId ? String(item.groupId).trim() : '';
        let labelText = key;
        if (dict && dict[key]) {
            labelText = dict[key];
        }
        return {
            ...item,
            groupName: labelText,
            label: labelText,
            name: labelText,
            group: labelText,
            groupId: labelText
        };
    });

    const topValue = sortedDetails.length > 0 ? sortedDetails[0].value : actualData.value;

    return {
        ...actualData,
        details: sortedDetails,
        value: topValue
    };
};

export default {
    title: 'CSAT - CES',
    description: 'Dashboard de resultados Encuesta CSAT - CES',
    backgroundClassName: (filters: any) => 'epsilon-hero-bg',
    widgets: (filters: any, filtersData: Record<string, any>) => {
        return [

            // -----------------------------------------------------------
            // SATISFACCIÓN Y ESFUERZO
            // -----------------------------------------------------------
            { title: 'MÉTRICAS PRINCIPALES', type: 'label', size: [4, 1], config: { color: '#B7AADC' } },

            getCommonWidget({
                commonWidgets,
                name: 'countNumberSemicircle',
                title: '¿Qué tan satisfecho estás con el servicio proporcionado por NetPay?',
                size: [2, 2],
                key: 'csat-satisfaccion',
                indicator: 'np_csat_ces_satisfaccion',
                extras: { formula: 'count-number', model: 'avg' }, // Forzamos conteo para graficar distribución 1-5
                mapper: (data: any) => mapGaugeData(data, CSAT_LABELS),
                extraConfigs: {
                    showValue: true,
                    valuePosition: 'inner',
                    center: ['50%', '75%'],
                    radius: ['60%', '115%'],
                    decimals: 0,
                    itemHeight: 14,
                    padAngle: 10,
                    colors: NETPAY_SCALE_5_COLORS,
                    itemBorderRadius: 5,
                    legendOrient: 'horizontal',
                    fontSize: 45,
                }
            }),

            getCommonWidget({
                commonWidgets,
                name: 'countNumberSemicircle',
                title: '¿Qué tanto esfuerzo te tomó que tu solicitud fuera resuelta?',
                size: [2, 2],
                key: 'ces-facilidad',
                indicator: 'np_csat_ces_facilidad',
                extras: { formula: 'count-number', model: 'avg' }, // Forzamos conteo para graficar distribución 1-5
                mapper: (data: any) => mapGaugeData(data, CES_LABELS),
                extraConfigs: {
                    showValue: true,
                    valuePosition: 'inner',
                    center: ['50%', '75%'],
                    radius: ['60%', '115%'],
                    decimals: 0,
                    itemHeight: 14,
                    padAngle: 10,
                    colors: NETPAY_SCALE_5_COLORS,
                    itemBorderRadius: 5,
                    legendOrient: 'horizontal',
                    fontSize: 45,
                }
            }),

            // -----------------------------------------------------------
            // ETIQUETAS DE SENTIMIENTO Y COMENTARIOS
            // -----------------------------------------------------------
            { title: 'ANÁLISIS DE SENTIMIENTOS Y COMENTARIOS', type: 'label', size: [4, 1], config: { color: '#B7AADC' } },

            // --- 1. Análisis de Sentimientos Global ---
            getCommonWidget({
                commonWidgets: commonWidgets,
                name: 'textAnalysis',
                size: [4, 2],
                key: 'analisis-sentimientos-nlp-csat',
                indicator: 'sentiment',
                extraConfigs: {
                    colors: NETPAY_SCALE_3_COLORS,
                    typeColumn: 'percent',
                    widgetTitle: 'Etiquetas de Sentimientos',
                    groupKeys: ['positive', 'neutral', 'negative'],
                    groupLabels: { 'positive': 'Positivos', 'neutral': 'Neutros', 'negative': 'Negativos' },
                    defaultHeight: 300,
                },
                mapper: (data: any, extra?: any) => {
                    const actualData = Array.isArray(data) ? data[0] : data;
                    if (!actualData || !actualData.categories) return { ...actualData, categories: List([]) };

                    let categories = actualData.categories || [];
                    let sortedCategories = [...categories].sort((a: any, b: any) => b.count - a.count);

                    const splitTags = (item: any[]): any[] => {
                        const tags = [...(item || [])].sort((a: any, b: any) => b.count - a.count);
                        return tags.map((tag: any) => ({
                            ...tag,
                            tags: splitTags(tag.tags),
                        }));
                    };

                    sortedCategories = sortedCategories.map((item: any) => ({
                        ...item,
                        tags: splitTags(item.tags),
                    }));

                    return {
                        ...actualData,
                        categories: List(sortedCategories),
                    }
                },
            }),

            { title: '', type: 'label', size: [4, 1], config: { color: 'transparent' } },

            // --- 2. Comentarios de Mejora ---
            {
                ...getCommonWidget({
                    commonWidgets,
                    name: 'lastComments',
                    title: 'Por favor, indícanos qué más podríamos hacer para mejorar tu experiencia con NetPay en la resolución de este ticket',
                    size: [4, 2],
                    key: 'csat-ces-comentario',
                    indicator: 'np_csat_ces_comment',
                    helpText: '<p>Muestra los comentarios más recientes del ticket.</p>',
                    extraConfigs: {
                        maxCommentSize: 200,
                        layout: 'card',
                        showScoreBadge: true,
                        scoreColors: NETPAY_SCALE_3_COLORS,
                        geolocationLabelShow: false,
                    },
                    mapper: (data: any) => {
                        // Reutilizamos el truco de CSS global para matar el scroll
                        if (typeof document !== 'undefined' && !document.getElementById('csat-comments-auto-height')) {
                            const style = document.createElement('style');
                            style.id = 'csat-comments-auto-height';
                            style.innerHTML = `
                              .csat-auto-height { height: auto !important; }
                              .csat-auto-height .epsilon-widget-card-component { height: auto !important; min-height: 100%; }
                              .csat-auto-height .comments-component--card { height: auto !important; }
                              .csat-auto-height .comments-card-list { max-height: none !important; overflow-y: visible !important; }
                          `;
                            document.head.appendChild(style);
                        }
                        return data;
                    }
                }),
                className: 'csat-auto-height'
            }
        ]
    },
} as DasboardConfig;